import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import * as moment from 'moment';

import { MyCampApp } from '../../api/common/enumerations';

import RecurrenceType = MyCampApp.RecurrenceType;
import NotificationType = MyCampApp.NotificationType;
import { NotificationItem } from '../../api/notification/notification-item.model';

export class ValidationUtil
{
    public static URL_PATTERN = '^((http|https):\/\/)([a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5})(:[0-9]{1,5})?(\/.*)?$';

    private static validationMessages: any = {
        required: 'Please enter a value for {controlName}.',
        email: 'Please enter a valid email address.',
        minlength: '{controlName} must contain at least {length} characters.',
        maxlength: '{controlName} has a maximum of {length} characters.',
        min: '{controlName} has a minimum value of {length}.',
        max: '{controlName} has a maximum value of {length}.',
        pattern: '{controlName} {customMessage}',
        phone: 'Please enter a valid phone number.',
        callToAction: 'Please enter a valid link address starting with "https://" or "mailto:" and must not contain spaces',
        illegalCharacters: 'The following characters are not allowed: {illegalCharacters}',
        requiredDateAndTime: 'Please enter a valid date and time for {controlName}.',
        requiredSubject: 'Please enter a value for the Subject for {controlName}.',
        subjectMaxLength: 'The Subject for {controlName} has a max length of {length} characters.',
        requiredText: 'Please enter a value for the Text for {controlName}.',
        textMaxLength: 'The Text for {controlName} has a max length of {length} characters.',
        requiredInterval: 'Please enter a value for the Repeat Interval.',
        requiredDaysOfWeek: 'Please select one or more days of the week.',
        requiredDaysOfMonth: 'Please select one or more days of the month.',
        unmatchedPasswords: 'Password and Confirm Password do not match',
        startDateAfterEndDate: 'The Start Date is after the End Date.',
    };

    public static setupValidation(component: any, form: FormGroup, controlName: string, messageLabel?: string)
    {
        if (messageLabel == null) messageLabel = controlName;
        const control = form.get(controlName);
        control?.valueChanges.pipe(debounceTime(1000)).subscribe( () =>
        {
            component[controlName + 'ValidationMessage'] = ValidationUtil.getValidationMessage(control, controlName, messageLabel);
        });
    }

    public static setupGroupedValidation(component: any, form: FormGroup, controlName: string, groupName: string)
    {
        const control = form.get(controlName);
        control?.valueChanges.pipe(debounceTime(1000)).subscribe( () =>
        {
            component[groupName + 'ValidationMessage'] = ValidationUtil.getValidationMessage(control, controlName, groupName);
        });
    }

    public static getValidationMessage(control: AbstractControl, controlName: string, messageLabel: string | undefined)
    {
        if ((!control.touched && !control.dirty) || !control.errors) return null;

        return Object.keys(control.errors)
            .map(key =>
            {
                let message = this.validationMessages[key].replace('{controlName}', messageLabel);
                if (key === 'minlength' || key === 'maxlength' || key === 'subjectMaxLength' || key === 'textMaxLength') message = message.replace('{length}', control.errors![key].requiredLength);
                if (key === 'min') message = message.replace('{length}', control.errors![key].min);
                if (key === 'max') message = message.replace('{length}', control.errors![key].max);
                if (key === 'pattern') message = message.replace('{customMessage}', control.errors![key].errorMessage);
                if (key === 'illegalCharacters') message = message.replace('{illegalCharacters}', control.errors![key].illegalCharacters);
                return message;
            })
            .join(' ');
    }

    public static dateAndTimeRequired(c: AbstractControl): { [ key: string ]: boolean } | null
    {
        if (c.value == null) return { requiredDateAndTime: true };
        return null;
    }

    public static phoneNumberValidation(c: AbstractControl): { [ key: string ]: boolean } | null
    {
        if (c.value == null || c.value.length === 0) return null;
        // noinspection RegExpRedundantEscape
        const reg = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im;
        if (!reg.test(c.value))
        {
            return { phone: true };
        }
        return null;
    }

    public static phoneNumberWithExtensionValidation(c: AbstractControl): { [ key: string ]: boolean } | null
    {
        if (c.value == null || c.value.length === 0) return null;
        // noinspection RegExpRedundantEscape
        const reg = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}( x\d{1,6})?$/im;
        if (!reg.test(c.value))
        {
            return { phone: true };
        }
        return null;
    }

    public static patternValidation(pattern: string, errorMessage: string, ignoreZeroLength = true): ValidatorFn {
        return (c: AbstractControl): ValidationErrors | null => {
            if (c.value == null || (ignoreZeroLength && c.value.length === 0)) { return null; }

            const re = new RegExp(pattern);
            if (!re.test(c.value)) {
                return { pattern: { pattern, errorMessage } };
            }
            return null;
        };
    }

    public static illegalCharactersValidation(illegalCharacters: string[]): ValidatorFn {
        return (c: AbstractControl): ValidationErrors | null => {
            if (c.value == null || c.value.length === 0) return null;

            if (illegalCharacters.some(ic => c.value.indexOf(ic) !== -1)) {
                return { illegalCharacters: { illegalCharacters: illegalCharacters.join(' ') } };
            }
            return null;
        };
    }

    public static callToActionValidation(c: AbstractControl): { [ key: string ]: boolean } | null
    {
        if (c.value == null || c.value.length === 0) return null;
        // noinspection RegExpRedundantEscape
        const reg = /^(mailto:|https:\/\/)[^ "]+$/im;
        if (!reg.test(c.value) || c.value.indexOf(' ') !== -1)
        {
            return { callToAction: true };
        }
        return null;
    }


    // noinspection TsLint
    public static notificationValidation(orgNameLength: number): ValidatorFn
    {
        return (c: AbstractControl): { [ key: string ]: any } | null =>
        {
            const notification = c.value as NotificationItem;
            if (notification == null) return null;

            if (notification.Type.toString() === NotificationType.Email.toString())
            {
                if (notification.Title == null || notification.Title.trim().length === 0) return { requiredSubject: true };
                if (notification.Title != null && notification.Title.length > NotificationItem.TitleMaxLength) return { subjectMaxLength: { requiredLength: NotificationItem.TitleMaxLength } };
                if (notification.Text != null && notification.Text.length > NotificationItem.EmailTextMaxLength) return { textMaxLength: { requiredLength: NotificationItem.EmailTextMaxLength } };
            }
            else if (notification.Type.toString() === NotificationType.Push.toString())
            {
                if (notification.Text != null && notification.Text.length > NotificationItem.PushTextMaxLength) return { textMaxLength: { requiredLength: NotificationItem.PushTextMaxLength } };
            }
            else if (notification.Type.toString() === NotificationType.Sms.toString())
            {
                if (notification.Text != null && notification.Text.length > NotificationItem.SmsTextMaxLength - orgNameLength - 2) return { textMaxLength: { requiredLength: NotificationItem.SmsTextMaxLength - orgNameLength - 2 } };
            }

            if (notification.Text == null || notification.Text.trim().length === 0) return { requiredText: true };
            if (notification.SendInfos.length === 0 && notification.Type !== NotificationType.Sms) return { requiredDateAndTime: true };

            return null;
        };
    }

    /* tslint:disable */
    public static recurrenceValidation(c: AbstractControl): { [ key: string ]: boolean } | null
    {
        const recurrence  = c.value as any;
        if (recurrence.RecurrenceType == RecurrenceType.Never) return null;

        if (recurrence.Interval == null)
            return { requiredInterval: true };
        if (recurrence.RecurrenceType == RecurrenceType.Weekly && recurrence.DaysOfWeekMask == undefined)
            return { requiredDaysOfWeek: true };
        // if (recurrence.RecurrenceType == RecurrenceType.Monthly && recurrence.RecurrenceDayOfMonthType == 0 && recurrence.DaysOfMonthMask == undefined)
        //     return { requiredDaysOfMonth: true };

        return null;
    }

    public static passwordMatchValidation(formGroup: FormGroup): { [ key: string ]: boolean } | null
    {
        const password = formGroup.get('Password')!.value;
        const confirmPassword = formGroup.get('ConfirmPassword')!.value;

        return password === confirmPassword ? null : { unmatchedPasswords: true };
    }

    public static startAndEndDateValidation(formGroup: FormGroup): { [ key: string ]: boolean } | null
    {
        const startDate = formGroup.get('StartDate')!.value;
        const endDate = formGroup.get('EndDate')!.value;
        if (startDate == null || endDate == null) return null;

        return moment(startDate).isAfter(endDate) ? { startDateAfterEndDate: true } : null;
    }

    static forceValueUpdateForForm(form: FormGroup, value: any, controlName: string): void
    {
        if (form == null) return;
        const control = form.get(controlName);
        if (control == null) return;
        control.setValue(value, { emitEvent: true });
        control.markAsDirty();
    }
}
